import { QueryKey, useQuery } from '@tanstack/react-query';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { EventRequestCategory } from '@cineamo/api/constants/EventRequestCategory.enum';

import { getEventRequestById } from '@/utils/api/event-request/event-request-api';

import {
    defaultMovieBackdropPlaceholder,
    defaultMoviePosterPlaceholder,
    getCategorySpecificImage
} from '@/helpers/event-request-helper';
import { getAgeRatingForISO_3166_2 } from '@cineamo/ui/src/helpers/age-rating-helper';
import { getISO_3166_2 } from '@cineamo/ui/src/helpers/cinema-helper';

import {
    CachedContentData,
    CachedContentDataAdapters,
    getCachedContentData as getSharedCachedContentData
} from '@cineamo/composables/src/hooks/content/useCachedContentData';

import { getCustomContent } from '@/utils/custom-content';

export type { CachedContentData } from '@cineamo/composables/src/hooks/content/useCachedContentData';

type CachedContentDataReturn = {
    data: CachedContentData | undefined;
};

/**
 * What this website adds on top of the shared cached-content resolution: its hand-maintained
 * per-content overrides, its event-request artwork and category placeholders, and the region-specific
 * age rating (a single-cinema site always knows which region it is in, so it can resolve it up front
 * rather than in a hook). The name/description/artwork resolution itself is the shared one.
 */
const CINEMA_WEBSITE_ADAPTERS: CachedContentDataAdapters = {
    getCustomContent: (contentId) => getCustomContent(contentId),
    getCategoryImage: (category: EventRequestCategory, type) => getCategorySpecificImage(category, type),
    fallbackPosterImage: defaultMoviePosterPlaceholder,
    fallbackBackdropImage: defaultMovieBackdropPlaceholder,
    fetchEventRequest: async (eventRequestId) => getEventRequestById(eventRequestId),
    resolveRegionAgeRating: async (movieId: MovieDto['id'], cinema?: CinemaDto) =>
        (await getAgeRatingForISO_3166_2(movieId, getISO_3166_2(cinema) ?? undefined))?.rating,
    // On a single cinema's site the name it gave that showing ("Sneak Preview", "OmU-Fassung") is
    // the headline, above the movie's own title.
    titleResolution: 'showing-first'
};

export type GetCachedContentDataOptions = {
    showing?: ShowingDto;
    content: ContentDto;
    cinema: CinemaDto;
    locale: string;
    overrideReleaseDate?: string | false;
};

/** This website's binding of the shared resolution — same signature as before the extraction. */
export async function getCachedContentData(options: GetCachedContentDataOptions): Promise<CachedContentData> {
    return getSharedCachedContentData({ ...options, ...CINEMA_WEBSITE_ADAPTERS });
}

export function useCachedContentData<TKey extends QueryKey = QueryKey>(
    options: {
        content?: ContentDto;
        cinema: CinemaDto;
        locale: string;
        showing?: ShowingDto;
        overrideReleaseDate?: string;
    },
    deps?: TKey
): CachedContentDataReturn {
    const content = options.content;
    const { data } = useQuery({
        queryKey: [
            'cached-content-data',
            `contentId-${options.content?.id}`,
            `showingId-${options.showing?.id}`,
            `locale-${options.locale}`,
            ...(deps ?? []).map((dep) => JSON.stringify(dep))
        ],
        queryFn: () => (content ? getCachedContentData({ ...options, content }) : undefined),
        // A showing without content (e.g. a private screening) has nothing to resolve, and a query must not resolve to undefined.
        enabled: !!content
    });
    return { data };
}
